import { UserRole, UserStatus } from "../types";
import { UserActions } from "./UserActions";
import { UserDto } from "@/common/api/datasources/Api";
import {
  getUserRoleTranslationKey,
  getUserStatusTranslationKey,
} from "@/common/constants/translations";
import { UserTooltip } from "@/components/shared/UserTooltip/UserTooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { TFunction } from "i18next";
import { Link } from "react-router-dom";

export const getUserColumns = (t: TFunction): ColumnDef<UserDto>[] => {
  return [
    {
      id: "user",
      header: t("users.columns.user"),
      cell: ({ row }) => {
        const user = row.original;

        return (
          <UserTooltip user={user} size={UserTooltip.size.lg} />
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => t("users.columns.isActive"),
      cell: ({ row }) => {
        const isActive = row.getValue("isActive") as boolean;
        return (
          <Badge
            variant={isActive ? "default" : "secondary"}
            className={
              isActive
                ? "bg-green-100 text-green-800 hover:bg-green-100"
                : "bg-gray-100 text-gray-800 hover:bg-gray-100"
            }
          >
            {isActive
              ? t("users.status.options.active")
              : t("users.status.options.inactive")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "role",
      header: () => t("users.columns.role"),
      cell: ({ row }) => {
        const role = row.getValue("role") as UserRole;
        return (
          <Badge variant="secondary">
            {t(getUserRoleTranslationKey(role))}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status",
      header: () => t("users.columns.status"),
      cell: ({ row }) => {
        const status = row.getValue("status") as UserStatus;
        return (
          <Badge
            variant={
              status === UserStatus.ACTIVE
                ? "default"
                : status === UserStatus.PENDING
                  ? "secondary"
                  : "destructive"
            }
          >
            {t(getUserStatusTranslationKey(status))}
          </Badge>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: () => t("users.columns.createdAt"),
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as string;
        return format(new Date(date), "d MMMM yyyy");
      },
    },
    {
      id: "actions",
      header: () => t("users.columns.actions"),
      cell: ({ row }) => <UserActions user={row.original} />,
    },
  ];
};
